import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import TrainingShell from '../../components/capacitacion/TrainingShell';
import { SubmitResponse, trainingApi } from '../../services/trainingApi';

export default function TrainingResultado() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<SubmitResponse | null>(null);

  useEffect(() => {
    if (!attemptId) return;
    const raw = sessionStorage.getItem(`training-result-${attemptId}`);
    if (raw) {
      try {
        setResult(JSON.parse(raw));
        return;
      } catch {
        // continue to fetch
      }
    }
    // Fallback: fetch si no está en sessionStorage
    trainingApi.getAttempt(Number(attemptId)).then((detalle) => {
      if (detalle?.attempt) {
        setResult({
          attemptId: detalle.attempt.id,
          puntaje: detalle.attempt.puntaje,
          aprobado: detalle.aprobado,
          umbral: detalle.umbral,
          respuestas: detalle.attempt.respuestas,
        });
      }
    });
  }, [attemptId]);

  if (!result) {
    return (
      <TrainingShell>
        <p className="text-gray-400">Cargando resultado...</p>
      </TrainingShell>
    );
  }

  const Icon = result.aprobado ? CheckCircle2 : XCircle;

  return (
    <TrainingShell>
      <div className="max-w-2xl mx-auto">
        <div
          className={`rounded-xl border p-8 text-center ${
            result.aprobado
              ? 'border-green-500/40 bg-green-500/10'
              : 'border-red-500/40 bg-red-500/10'
          }`}
        >
          <Icon
            className={`h-16 w-16 mx-auto mb-3 ${
              result.aprobado ? 'text-green-400' : 'text-red-400'
            }`}
          />
          <h1 className="text-3xl font-bold mb-2">
            {result.aprobado ? '¡Aprobado!' : 'No aprobado'}
          </h1>
          <p className="text-gray-400 mb-4">
            Obtuviste <b className="text-white text-xl">{result.puntaje}%</b> ·
            Umbral requerido: {result.umbral}%
          </p>
        </div>

        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Revisión de respuestas</h2>
          <div className="space-y-3">
            {result.respuestas.map((r, idx) => (
              <div
                key={r.preguntaId}
                className={`rounded-lg border p-4 ${
                  r.correcta
                    ? 'border-green-500/30 bg-green-500/5'
                    : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div className="flex items-start gap-2 mb-1">
                  {r.correcta ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">
                      Pregunta {idx + 1} · tu respuesta:{' '}
                      <b className="uppercase">{r.respuesta}</b>
                      {!r.correcta && r.respuesta_correcta && (
                        <>
                          {' '}
                          · correcta: <b className="uppercase text-green-300">{r.respuesta_correcta}</b>
                        </>
                      )}
                    </p>
                    {r.explicacion && (
                      <p className="text-xs text-gray-400 mt-2 italic">{r.explicacion}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={() => navigate('/capacitacion/modulos')}
            className="inline-flex items-center gap-2 rounded-md bg-ngs-cyan text-ngs-bg font-semibold px-6 py-2.5 hover:bg-ngs-cyan/90"
          >
            Volver a los módulos
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </TrainingShell>
  );
}
